declare global {
  interface Window {
    ReactNativeWebView?: {
      postMessage: (message: string) => void;
    };
  }
}

const DEFAULT_SCOPE = "user.info.basic,user.info.profile,user.info.stats";
const STATE_STORAGE_KEY = "tikTokCsrfState";

const buildRedirectUri = () => {
  if (process.env.REACT_APP_WEB_URL) {
    return `${process.env.REACT_APP_WEB_URL}tiktok-auth`;
  }

  return undefined;
};

export const handleTikTokLogin = async (): Promise<void> => {
  const clientKey = process.env.REACT_APP_TIKTOK_KEY;
  const redirectUri = buildRedirectUri();
  const scope = DEFAULT_SCOPE;

  if (!clientKey) {
    throw new Error("TikTok client key is not configured.");
  }

  if (!redirectUri) {
    throw new Error("TikTok redirect URI is not configured.");
  }

  if (window.ReactNativeWebView) {
    window.ReactNativeWebView.postMessage(
      JSON.stringify({ type: "tiktok_auth" })
    );
    return;
  }

  const authBase = "https://www.tiktok.com/v2/auth/authorize/";

  const stateArray = new Uint8Array(30);
  window.crypto.getRandomValues(stateArray);
  const csrfState = Array.from(stateArray)
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");

  sessionStorage.setItem(STATE_STORAGE_KEY, csrfState);

  const encoder = new TextEncoder();
  const data = encoder.encode(csrfState);
  const hashBuffer = await window.crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hash = hashArray
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");

  let url = `${authBase}?client_key=${clientKey}`;
  url += `&scope=${encodeURIComponent(scope)}`;
  url += "&response_type=code";
  url += `&redirect_uri=${redirectUri}`;
  url += `&state=${csrfState}`;
  url += `&code_challenge=${hash}`;
  url += "&code_challenge_method=S256";
  console.log(url);

  const width = 560;
  const height = 720;
  const dualScreenLeft =
    window.screenLeft !== undefined ? window.screenLeft : window.screenX;
  const dualScreenTop =
    window.screenTop !== undefined ? window.screenTop : window.screenY;
  const currentWidth =
    window.innerWidth || document.documentElement.clientWidth || width;
  const currentHeight =
    window.innerHeight || document.documentElement.clientHeight || height;
  const systemZoomX =
    window.screen?.availWidth && window.screen.availWidth !== 0
      ? currentWidth / window.screen.availWidth
      : 1;
  const systemZoomY =
    window.screen?.availHeight && window.screen.availHeight !== 0
      ? currentHeight / window.screen.availHeight
      : 1;
  const systemZoom = Math.min(systemZoomX || 1, systemZoomY || 1);
  const left = dualScreenLeft + (currentWidth - width) / 2 / (systemZoom || 1);
  const top = dualScreenTop + (currentHeight - height) / 2 / (systemZoom || 1);

  const popupFeatures = [
    `width=${width}`,
    `height=${height}`,
    `top=${Math.round(top)}`,
    `left=${Math.round(left)}`,
    "toolbar=no",
    "location=no",
    "directories=no",
    "status=no",
    "menubar=no",
    "scrollbars=yes",
    "resizable=yes",
  ].join(",");

  const newWindow = window.open(url, "tiktokAuthPopup", popupFeatures);
  if (newWindow) {
    newWindow.opener = null;
    newWindow.focus();
    return;
  }

  window.location.href = url;
};
